import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * parsePrazoToDate (unificacao_resumo_fup 2026-04-26):
 * Best-effort. Extrai ISO date 'YYYY-MM-DD' de texto livre.
 *   "ate 25/04/2026"  → "2026-04-25"
 *   "25/04/26"        → "2026-04-25" (YY < 50 → 20YY, senao 19YY)
 *   "Urgente — 2 dias" → null
 * Valida data real. Retorna null quando nao consegue.
 */
const parsePrazoToDate = (texto) => {
  if (!texto || typeof texto !== 'string') return null;
  const isoMatch = texto.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const dt = new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`);
    if (!isNaN(dt.getTime())) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  const brMatch = texto.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (brMatch) {
    let [, d, m, y] = brMatch;
    if (y.length === 2) y = (parseInt(y, 10) < 50 ? '20' : '19') + y;
    const dd = d.padStart(2, '0');
    const mm = m.padStart(2, '0');
    const dt = new Date(`${y}-${mm}-${dd}T00:00:00`);
    if (!isNaN(dt.getTime())
      && dt.getMonth() + 1 === parseInt(mm, 10)
      && dt.getDate() === parseInt(dd, 10)) {
      return `${y}-${mm}-${dd}`;
    }
  }
  return null;
};

/**
 * Hook de detalhes de uma oferta específica.
 *
 * v2 (Sessão 3.5a): 
 * - Emails filtrados por oferta_id (não mais por empresa_id) — corrige bug crítico
 *   de mistura de ofertas da mesma empresa
 * - Expõe emailsAmbiguos: emails da empresa sem oferta classificada (aguardando decisão humana)
 * - vincularEmailAOferta: move email de uma oferta para outra com trilha HITL
 * - marcarBriefingRevisado: remove flag 'revisar_apos_classificacao' após humano validar
 */
export const useOfertaDetalhe = (ofertaId) => {
  const [oferta, setOferta] = useState(null);
  const [emails, setEmails] = useState([]);
  const [emailsAmbiguos, setEmailsAmbiguos] = useState([]);
  const [timelineItems, setTimelineItems] = useState([]);
  const [briefingAtual, setBriefingAtual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!ofertaId) return;
    setLoading(true);
    setError(null);

    try {
      // 1. Oferta + joins
      const { data: ofertaData, error: eOferta } = await supabase
        .from('ofertas')
        .select(`
          *,
          empresas:empresa_id (id, empresa, company_name),
          areas:area_id (id, nome, localizacao_bairro, area_total_m2, descricao, palavras_chave),
          colaboradores:responsavel_id (id, nome, email),
          contato_negociador:contatos!ofertas_contato_negociador_id_fkey (id, first_name, last_name, title, eh_decisor)
        `)
        .eq('id', ofertaId)
        .single();

      if (eOferta) throw eOferta;
      setOferta(ofertaData);

      // 2. Emails DESTA oferta (filtrado por oferta_id) - correção crítica
      const { data: emailsRaw, error: eEmails } = await supabase
        .from('emails_gmail')
        .select('*')
        .eq('oferta_id', ofertaId)
        .order('data_envio', { ascending: false });

      if (eEmails) throw eEmails;

      // Dedup: prefer internet_message_id (cross-mailbox: mesma mensagem
      // ingerida por 2+ caixas de colaborador tem mesmo Message-Id RFC).
      // Fallback composto cobre rows legadas sem synthetic ID.
      const emailsData = [];
      const seenKeys = new Set();
      for (const em of (emailsRaw || [])) {
        const key = em.internet_message_id
          || `${em.from_email}|${em.subject}|${em.data_envio}`
          || em.gmail_msg_id
          || em.id;
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        emailsData.push(em);
      }
      setEmails(emailsData);

      // 3. Emails AMBÍGUOS da empresa (sem oferta_id mas da mesma empresa)
      // Aparecem para que humano possa arrastar/vincular à oferta correta
      if (ofertaData?.empresa_id) {
        const { data: ambigData } = await supabase
          .from('emails_gmail')
          .select('id, from_email, subject, data_envio, direcao, body_text, classificacao_status')
          .eq('empresa_id', ofertaData.empresa_id)
          .is('oferta_id', null)
          .order('data_envio', { ascending: false })
          .limit(20);
        setEmailsAmbiguos(ambigData || []);
      }

      // 4. Notas + briefings da oferta (deal_context)
      const { data: contextData, error: eContext } = await supabase
        .from('deal_context')
        .select('*')
        .eq('oferta_id', ofertaId)
        .order('created_at', { ascending: false });

      if (eContext) throw eContext;

      // Separa o briefing mais recente
      const briefings = (contextData || []).filter(c => c.tipo === 'briefing');
      setBriefingAtual(briefings[0] || null);

      // 4.b. Mensagens WhatsApp da oferta (ADR-022 Onda 2)
      // Tabela canônica: mensagens_whatsapp. data_envio é o timestamp do
      // export (cliente); captured_at registra quando entrou no CRM.
      const { data: whatsappData } = await supabase
        .from('mensagens_whatsapp')
        .select('id, texto, data_envio, direcao, telefone_contato, captured_by, captured_at, source_chat_label, parser_version, parse_confidence, classificacao_status, intake_status')
        .eq('oferta_id', ofertaId)
        .order('data_envio', { ascending: false });

      // Timeline consolidada (notas + briefings + emails) cronológica
      const timeline = [
        ...(contextData || []).map(c => ({
          kind: c.tipo,
          id: c.id,
          data: c.created_at,
          texto: c.texto,
          metadata: c.metadata,
          autor_tipo: c.autor_tipo,
          autor_id: c.autor_id,
          canal_origem: c.canal_origem,
        })),
        ...(emailsData || []).map(e => ({
          kind: 'email',
          id: e.id,
          data: e.data_envio,
          texto: e.body_text,
          from_email: e.from_email,
          to_emails: e.to_emails,
          cc_emails: e.cc_emails,
          subject: e.subject,
          direcao: e.direcao,
          lado: e.lado,
          gmail_msg_id: e.gmail_msg_id,
          remetente_tipo: e.remetente_tipo,
          tipo_conteudo: e.tipo_conteudo,
          empresa_id: e.empresa_id,
          empresa_proprietaria_id: e.empresa_proprietaria_id,
          area_mencionada_id: e.area_mencionada_id,
          oferta_id: e.oferta_id,
          classificacao_status: e.classificacao_status,
          classificacao_confianca: e.classificacao_confianca,
          classificado_por: e.classificado_por,
        })),
        ...(whatsappData || []).map(w => ({
          kind: 'whatsapp',
          id: w.id,
          data: w.data_envio,
          texto: w.texto,
          direcao: w.direcao,
          telefone_contato: w.telefone_contato,
          captured_by: w.captured_by,
          captured_at: w.captured_at,
          source_chat_label: w.source_chat_label,
          parser_version: w.parser_version,
          parse_confidence: w.parse_confidence,
          classificacao_status: w.classificacao_status,
          intake_status: w.intake_status,
        })),
      ].sort((a, b) => {
        const dateA = a.data ? new Date(a.data).getTime() : 0;
        const dateB = b.data ? new Date(b.data).getTime() : 0;
        return dateB - dateA;
      });

      setTimelineItems(timeline);
    } catch (err) {
      console.error('Erro ao buscar detalhe da oferta:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [ofertaId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // === Ações ===

  const adicionarNota = async (texto, userEmail) => {
    if (!texto?.trim() || texto.trim().length < 3) {
      throw new Error('Nota precisa ter pelo menos 3 caracteres');
    }
    const { data, error: err } = await supabase
      .from('deal_context')
      .insert({
        oferta_id: ofertaId,
        autor_tipo: 'colaborador',
        autor_id: userEmail || 'desconhecido',
        tipo: 'nota',
        canal_origem: 'manual',
        texto: texto.trim(),
        metadata: { criada_em: new Date().toISOString() },
      })
      .select()
      .single();
    if (err) throw err;
    await fetchAll();
    return data;
  };

  /**
   * Edita texto de uma nota existente.
   *
   * Preserva trilha em metadata.edits[] (lista de { texto_anterior, editado_em,
   * editado_por }) para que auditor consiga reconstruir o que mudou.
   */
  const editarNota = async (notaId, novoTexto, userEmail) => {
    if (!novoTexto?.trim() || novoTexto.trim().length < 3) {
      throw new Error('Nota precisa ter pelo menos 3 caracteres');
    }
    const { data: atual, error: eGet } = await supabase
      .from('deal_context')
      .select('texto, metadata')
      .eq('id', notaId)
      .single();
    if (eGet) throw eGet;

    const novaMetadata = { ...(atual?.metadata || {}) };
    const edits = Array.isArray(novaMetadata.edits) ? novaMetadata.edits.slice() : [];
    edits.push({
      texto_anterior: atual?.texto || '',
      editado_em: new Date().toISOString(),
      editado_por: userEmail || 'desconhecido',
    });
    novaMetadata.edits = edits;
    novaMetadata.editada_em = new Date().toISOString();
    novaMetadata.editada_por = userEmail || 'desconhecido';

    const { error: err } = await supabase
      .from('deal_context')
      .update({ texto: novoTexto.trim(), metadata: novaMetadata })
      .eq('id', notaId);
    if (err) throw err;
    await fetchAll();
  };

  /**
   * Vincula ou move um email para esta oferta (ou remove vínculo).
   * Registra trilha HITL: oferta_id_anterior, classificado_por, classificacao_status='humano'
   *
   * @param {string} emailId
   * @param {string|null} novaOfertaId - null desvincula
   * @param {string} userEmail - quem fez a mudança
   */
  const vincularEmailAOferta = async (emailId, novaOfertaId, userEmail) => {
    // Busca o email atual para preservar oferta_id_anterior
    const { data: emailAtual } = await supabase
      .from('emails_gmail')
      .select('oferta_id')
      .eq('id', emailId)
      .single();

    const patch = {
      oferta_id: novaOfertaId,
      oferta_id_anterior: emailAtual?.oferta_id || null,
      classificacao_status: novaOfertaId ? 'humano_classificado' : 'pendente',
      classificacao_confianca: novaOfertaId ? 1.0 : null,
      classificado_por: `humano:${userEmail || 'desconhecido'}`,
      classificado_em: new Date().toISOString(),
    };

    const { error: err } = await supabase
      .from('emails_gmail')
      .update(patch)
      .eq('id', emailId);

    if (err) throw err;
    await fetchAll();
  };

  /**
   * Aplica override humano em um campo do briefing.
   *
   * Strategy: briefing_completo é snapshot da IA (nunca sobrescrito).
   * Edições humanas vão em metadata.human_edits como overlay. O render
   * prioriza human_edits[campo] sobre briefing_completo[campo], mas um
   * regenerar do briefing preserva os overrides do humano.
   *
   * campos suportados: 'resumo' | 'temperatura' | 'estagio_sugerido' |
   * 'proximo_passo' | 'stakeholders' | 'riscos'
   */
  const atualizarHumanEditBriefing = async (briefingId, campo, novoValor, userEmail) => {
    const { data: atual, error: eGet } = await supabase
      .from('deal_context')
      .select('metadata, oferta_id')
      .eq('id', briefingId)
      .single();
    if (eGet) throw eGet;

    const meta = { ...(atual?.metadata || {}) };
    const humanEdits = { ...(meta.human_edits || {}) };
    if (novoValor === null || novoValor === undefined) {
      delete humanEdits[campo];
    } else {
      humanEdits[campo] = novoValor;
    }
    humanEdits._updated_at = new Date().toISOString();
    humanEdits._updated_by = userEmail || 'desconhecido';
    meta.human_edits = humanEdits;

    const { error: err } = await supabase
      .from('deal_context')
      .update({ metadata: meta })
      .eq('id', briefingId);
    if (err) throw err;

    // Frente B unificacao_resumo_fup 2026-04-26: quando humano edita 'resumo' no
    // BriefingEditor, propagar para ofertas.resumo_estagio como 'ai_editado'.
    // (Edicao direto na row do Dashboard ja passa por useOfertas.updateOferta
    // com fonte='manual' via buildHITLPatchManual.)
    if (campo === 'resumo' && novoValor !== null && novoValor !== undefined && atual?.oferta_id) {
      const valorStr = String(novoValor).trim();
      if (valorStr) {
        await supabase.from('ofertas').update({
          resumo_estagio: valorStr,
          resumo_estagio_fonte: 'ai_editado',
          resumo_estagio_updated_at: new Date().toISOString(),
          resumo_estagio_updated_by: userEmail || 'desconhecido',
        }).eq('id', atual.oferta_id).then(() => null).catch(e => console.warn('[briefing→ofertas resumo] propag nao-fatal:', e));
      }
    }

    // Frente C: quando humano edita proximo_passo (inclui prazo), tentar propagar
    // para ofertas.data_fup SE o FUP estiver vazio (humano nao governou manualmente).
    if (campo === 'proximo_passo' && novoValor && typeof novoValor === 'object' && atual?.oferta_id) {
      const prazoTexto = novoValor.prazo_sugerido;
      const parsed = parsePrazoToDate(prazoTexto);
      if (parsed) {
        const { data: ofertaAtual } = await supabase
          .from('ofertas')
          .select('data_fup, data_fup_updated_by')
          .eq('id', atual.oferta_id)
          .single();
        // So preenche se FUP vazio (humano nao governou ainda)
        if (!ofertaAtual?.data_fup) {
          await supabase.from('ofertas').update({
            data_fup: parsed,
            data_fup_sugerida_ia: parsed,
            data_fup_updated_at: new Date().toISOString(),
            data_fup_updated_by: userEmail || 'briefing_editor',
          }).eq('id', atual.oferta_id).then(() => null).catch(e => console.warn('[briefing→ofertas data_fup] propag nao-fatal:', e));
        } else {
          // FUP preenchido — so registra a sugestao IA sem sobrescrever
          await supabase.from('ofertas').update({
            data_fup_sugerida_ia: parsed,
          }).eq('id', atual.oferta_id).then(() => null).catch(() => null);
        }
      }
    }

    await fetchAll();
  };

  /**
   * Marca um briefing antigo como revisado (remove flag revisar_apos_classificacao).
   * Humano confirmou que leu e o briefing ainda é válido (ou regenerou e o novo é bom).
   */
  const marcarBriefingRevisado = async (briefingId, userEmail) => {
    const { data: atual } = await supabase
      .from('deal_context')
      .select('metadata')
      .eq('id', briefingId)
      .single();

    const novaMetadata = { ...(atual?.metadata || {}) };
    delete novaMetadata.revisar_apos_classificacao;
    delete novaMetadata.motivo_revisao;
    novaMetadata.revisado_em = new Date().toISOString();
    novaMetadata.revisado_por = userEmail || 'desconhecido';

    const { error: err } = await supabase
      .from('deal_context')
      .update({ metadata: novaMetadata })
      .eq('id', briefingId);

    if (err) throw err;
    await fetchAll();
  };

  const gerarBriefing = async () => {
    const { data, error: err } = await supabase.functions.invoke('generate-briefing', {
      body: { oferta_id: ofertaId },
    });
    if (err) throw err;

    // Frente B+C unificacao_resumo_fup 2026-04-26:
    // Apos a IA gerar novo briefing (persistido em deal_context pela edge function),
    // propagar para ofertas.resumo_estagio e ofertas.data_fup — respeitando a
    // precedencia: NUNCA sobrescrever 'manual'. Isso e feito aqui (no hook) em vez de
    // dentro do generate-briefing porque essa edge function nao esta no repo local
    // e deploy manual seria disruptivo. O hook faz:
    //   1. SELECT do briefing recem-gerado (tipo=briefing, order created_at DESC)
    //   2. SELECT da oferta atual para conhecer resumo_estagio_fonte + data_fup atual
    //   3. UPDATE condicional em ofertas.
    try {
      const { data: briefings } = await supabase
        .from('deal_context')
        .select('metadata')
        .eq('oferta_id', ofertaId)
        .eq('tipo', 'briefing')
        .order('created_at', { ascending: false })
        .limit(1);
      const latest = (briefings || [])[0];
      const completo = latest?.metadata?.briefing_completo || {};
      const humanEdits = latest?.metadata?.human_edits || {};

      const { data: ofertaAtual } = await supabase
        .from('ofertas')
        .select('resumo_estagio_fonte, data_fup')
        .eq('id', ofertaId)
        .single();

      const patch = {};

      // Regerar briefing e ACAO EXPLICITA do usuario — IA sobrescreve SEMPRE
      // (inclusive valores manuais). Se humano quiser preservar, edita novamente
      // depois. Regra documentada em 2026-04-26 (fix_regerar_propaga).
      const resumoIA = humanEdits.resumo !== undefined ? humanEdits.resumo : completo.resumo;
      if (resumoIA) {
        patch.resumo_estagio = String(resumoIA).trim();
        patch.resumo_estagio_fonte = 'ai_briefing';
        patch.resumo_estagio_original_ia = String(resumoIA).trim();
        patch.resumo_estagio_updated_at = new Date().toISOString();
        patch.resumo_estagio_updated_by = 'agent_generate_briefing';
      }

      // Data FUP: regerar sempre aplica o prazo parseavel (acao explicita).
      const proximoPasso = humanEdits.proximo_passo !== undefined ? humanEdits.proximo_passo : completo.proximo_passo;
      const prazoTexto = proximoPasso?.prazo_sugerido;
      const prazoParsed = parsePrazoToDate(prazoTexto);
      if (prazoParsed) {
        patch.data_fup_sugerida_ia = prazoParsed;
        patch.data_fup = prazoParsed;
        patch.data_fup_updated_at = new Date().toISOString();
        patch.data_fup_updated_by = 'agent_generate_briefing';
      }

      if (Object.keys(patch).length > 0) {
        await supabase.from('ofertas').update(patch).eq('id', ofertaId);
      }
    } catch (e) {
      console.warn('[gerarBriefing] propagacao briefing→ofertas nao-fatal:', e);
    }

    await fetchAll();
    return data;
  };

  return {
    oferta,
    emails,
    emailsAmbiguos,
    timelineItems,
    briefingAtual,
    loading,
    error,
    refetch: fetchAll,
    adicionarNota,
    editarNota,
    vincularEmailAOferta,
    marcarBriefingRevisado,
    atualizarHumanEditBriefing,
    gerarBriefing,
  };
};
